import '../../App.css';
import FileUploadComponent from '../../components/FileUploadComponent';
import EmailPreview from '../../components/EmailPreview';
import UploadStatus from '../../components/UploadStatus';
import ValidationSummary from '../../components/ValidationSummary';
import Stepper from '../../components/Stepper/Stepper';
import StepContent from '../../components/StepContent/StepContent';
import LoadingOverlay from '../../components/LoadingOverlay';
import Tabs from '../../components/Tabs/Tabs';
import useFileUpload from '../../hooks/useFileUpload';
import { getConfig } from '../../config';
import { useMemo } from 'react';

// Workflow steps configuration
const WORKFLOW_STEPS = [
  {
    id: 1,
    label: 'Upload a file',
    description: 'Select and upload your email file'
  },
  {
    id: 2,
    label: 'Validate email',
    description: 'Review and validate email addresses'
  },
  {
    id: 3,
    label: 'Store into DB',
    description: 'Save validated emails to database'
  }
];

function GeneralApp() {
  const { parseFile, validateEmails, uploadEmails, isLoading, isValidating, isUploading, result, error, parsedEmails, fileMetadata, uploadProgress, validationResults, validationSummary, validationProgress, reset } = useFileUpload();
  const config = getConfig();

  // Calculate current step and completed steps based on workflow state
  const { currentStep, completedSteps } = useMemo(() => {
    // Stage 1: Initial state (no file uploaded)
    if (!parsedEmails) {
      return { currentStep: 1, completedSteps: new Set() };
    }

    // Stage 2: File parsed, need to validate
    if (parsedEmails && !validationResults) {
      return { currentStep: 2, completedSteps: new Set([1]) };
    }

    // Stage 3: Validation complete, ready to upload or uploading
    if (validationResults && !result) {
      return { currentStep: 3, completedSteps: new Set([1, 2]) };
    }

    // Stage 3: Upload complete
    if (result) {
      return { currentStep: 3, completedSteps: new Set([1, 2, 3]) };
    }

    // Default to stage 1
    return { currentStep: 1, completedSteps: new Set() };
  }, [parsedEmails, validationResults, result]);

  const handleFileSelected = async (file) => {
    await parseFile(file);
  };

  const handleValidate = async () => {
    await validateEmails();
  };

  const handleConfirmUpload = async (selectedRows, selectedIndices) => {
    await uploadEmails(selectedRows, selectedIndices);
  };

  const handleCancelPreview = () => {
    reset();
  };

  const handleReset = () => {
    reset();
  };

  return (
    <div className="App">
      {/* Loading Overlay - shown during file parsing and validation */}
      {isLoading && (
        <LoadingOverlay
          message="Processing File"
          subMessage="Parsing and extracting email addresses from your file..."
        />
      )}
      {isValidating && (
        <LoadingOverlay
          message="Validating Emails"
          subMessage="Checking email addresses against Symphony Summit API..."
          progress={validationProgress}
        />
      )}

      {/* Stepper */}
      <Stepper
        steps={WORKFLOW_STEPS}
        currentStep={currentStep}
        completedSteps={completedSteps}
      />

      {/* Main Content Area */}
      <main className="app-main">
        <StepContent currentStep={currentStep}>
          <div className="upload-section">
            {/* Stage 1: File Upload */}
            {currentStep === 1 && (
              <>
                {/* Display file upload error if present */}
                {error && !isLoading && (
                  <div className="error-message" style={{
                    backgroundColor: '#fee',
                    border: '1px solid #fcc',
                    borderRadius: '4px',
                    padding: '12px 16px',
                    marginBottom: '16px',
                    color: '#c33'
                  }}>
                    <strong>Upload Error:</strong> {error}
                  </div>
                )}
                <FileUploadComponent
                  maxFileSize={config.maxFileSize}
                  allowedTypes={config.allowedTypes}
                  onUploadComplete={handleFileSelected}
                />
              </>
            )}

            {/* Stage 2: Email Preview and Validation */}
            {currentStep === 2 && parsedEmails && (
              <>
                {/* Display validation error if present */}
                {error && !isValidating && (
                  <div className="error-message" style={{
                    backgroundColor: '#fee',
                    border: '1px solid #fcc',
                    borderRadius: '4px',
                    padding: '12px 16px',
                    marginBottom: '16px',
                    color: '#c33'
                  }}>
                    <strong>Validation Error:</strong> {error}
                  </div>
                )}

                {validationSummary && validationResults ? (
                  <Tabs
                    tabs={[
                      {
                        label: 'Validation Summary',
                        content: (
                          <ValidationSummary
                            summary={validationSummary}
                            validationResults={validationResults}
                          />
                        )
                      },
                      {
                        label: 'Review Emails',
                        badge: parsedEmails.totalRows,
                        content: (
                          <EmailPreview
                            emailData={parsedEmails}
                            fileName={fileMetadata?.fileName || 'Unknown'}
                            onConfirm={handleConfirmUpload}
                            onCancel={handleCancelPreview}
                            onValidate={handleValidate}
                            isValidating={isValidating}
                            isUploading={isUploading}
                            validationResults={validationResults}
                          />
                        )
                      }
                    ]}
                    defaultTab={0}
                  />
                ) : (
                  <EmailPreview
                    emailData={parsedEmails}
                    fileName={fileMetadata?.fileName || 'Unknown'}
                    onConfirm={handleConfirmUpload}
                    onCancel={handleCancelPreview}
                    onValidate={handleValidate}
                    isValidating={isValidating}
                    isUploading={isUploading}
                    validationResults={validationResults}
                  />
                )}
              </>
            )}

            {/* Stage 3: Ready to Upload or Upload Status */}
            {currentStep === 3 && (
              <>
                {/* Show validated email preview before upload */}
                {!result && parsedEmails && validationResults && validationSummary && (
                  <Tabs
                    tabs={[
                      {
                        label: 'Validation Summary',
                        content: (
                          <ValidationSummary
                            summary={validationSummary}
                            validationResults={validationResults}
                          />
                        )
                      },
                      {
                        label: 'Review Emails',
                        badge: parsedEmails.totalRows,
                        content: (
                          <EmailPreview
                            emailData={parsedEmails}
                            fileName={fileMetadata?.fileName || 'Unknown'}
                            onConfirm={handleConfirmUpload}
                            onCancel={handleCancelPreview}
                            onValidate={handleValidate}
                            isValidating={isValidating}
                            isUploading={isUploading}
                            validationResults={validationResults}
                          />
                        )
                      }
                    ]}
                    defaultTab={0}
                  />
                )}

                {/* Show upload status after upload */}
                {result && (
                  <>
                    <UploadStatus
                      isLoading={isUploading}
                      result={result}
                      error={error}
                      uploadProgress={uploadProgress}
                    />

                    {!isUploading && (
                      <div className="action-buttons">
                        <button
                          className="reset-button"
                          onClick={handleReset}
                          aria-label="Upload another file"
                        >
                          Upload Another File
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </StepContent>
      </main>
    </div>
  );
}

export default GeneralApp;
